import type { ReactNode } from "react";
import Tooltip from "@mui/material/Tooltip";
import cx from "classnames";
import WrapIf from "src/components/utils/WrapIf/WrapIf";
import {
  DEFAULT_ARIA_LABEL,
  DEFAULT_PH_ACTION,
  DEFAULT_TOOLTIP,
  FAB_TRIGGER_SIZE_DEFAULT,
  GLYPH_RATIO,
} from "./FabButtonTrigger.constants";

type FabButtonTriggerProps = {
  open: boolean;
  onClick: () => void;
  /** Box size in px. Drives width/height and the proportional glyph. */
  size?: number;
  /** Trigger content. Defaults to the help "?" glyph. */
  children?: ReactNode;
  /** Accessible label for the button. */
  ariaLabel?: string;
  /** Tooltip shown while collapsed. Falsy disables the tooltip wrapper. */
  tooltip?: ReactNode;
  /** PostHog autocapture action (`data-ph-capture-attribute-action`). */
  phAction?: string;
  className?: string;
};

const FabButtonTrigger = ({
  open,
  onClick,
  size = FAB_TRIGGER_SIZE_DEFAULT,
  children = "?",
  ariaLabel = DEFAULT_ARIA_LABEL,
  tooltip = DEFAULT_TOOLTIP,
  phAction = DEFAULT_PH_ACTION,
  className,
}: FabButtonTriggerProps) => {
  const px = Math.max(0, size);
  return (
    <WrapIf
      condition={!open && !!tooltip}
      With={Tooltip}
      title={tooltip}
      arrow
      disableInteractive
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-hidden={open}
        tabIndex={open ? -1 : 0}
        data-ph-capture-attribute-action={phAction}
        style={{ width: px, height: px }}
        className={cx(
          // Shrink in lockstep with the disc: same token (duration-500 easing-spring-soft)
          // and the same hover override (250) so the two stay aligned even mid-hover.
          "absolute bottom-0 right-0 grid cursor-pointer place-items-center rounded-full transition-[width,height] duration-500 easing-spring-soft group-hover/fab:duration-250 motion-reduce:transition-none",
          open ? "pointer-events-none" : "pointer-events-auto",
          className
        )}
      >
        <span
          style={{ fontSize: px * GLYPH_RATIO }}
          className={cx(
            "select-none font-bold leading-none text-white transition-all duration-200 motion-reduce:transition-none",
            open ? "scale-50 opacity-0" : "scale-100 opacity-100"
          )}
        >
          {children}
        </span>
      </button>
    </WrapIf>
  );
};

export default FabButtonTrigger;
